import React, { useContext, useEffect, useState } from "react";
import {
   Button, ButtonGroup, Col, Input, Label,
   Modal, ModalBody, ModalFooter, ModalHeader,
   Row, Table,
} from "reactstrap";
import DebounceInput from "react-debounce-input";
import Select from "react-select";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SingleDatePicker, ItemDropdownMenu } from "./";
import { dateHelpers, helpers, UserContext } from "../utils";

const { validateLineItemQty, alertMinQty } = helpers;

export default function CartProductCard(props) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   const { allowInlineEditsDeletes } = props;
   const [cartItems, setCartItems] = useState([]);
   const [cardSubtotal, setCardSubtotal] = useState(0.0);
   const [confirmDelete, setConfirmDelete] = useState(false);
   const [deleteIndex, setDeleteIndex] = useState(null);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [showOverride, setShowOverride] = useState(false);
   const modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";
   const isCustomer = userCtx && userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole;

   useEffect(() => {
      if (parseInt(userCtx.openLineItem, 10) >= 0) {
         setEditIndex(parseInt(userCtx.openLineItem, 10));
         allowEdit(parseInt(userCtx.openLineItem, 10));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userCtx.openLineItem]);

   useEffect(() => {
      if (!_.isEqual(props.cartItems, cartItems)) {
         setCartItems(props.cartItems);
         setCardSubtotal(
            _.reduce(
               props.cartItems,
               (sum, item) => { return sum + item.extendedPrice; },
               0
            )
         );
      }

      setShowOverride(props.showPriceOverride);
   }, [props, cartItems]);

   function allowEdit(index) {
      setEditIndex(index);
      if (_.isFunction(props.setIsEditing)) {
         props.setIsEditing(true);
      }
      if (!editCopy || !editCopy.itemNumber) {
         const itemCopy = Object.assign({}, cartItems[index]);
         itemCopy.siteDLL = _.map(itemCopy.possibleOrderSites, (site) => {
            return {
               value: site.siteId,
               label: site.siteWithDescription
            };
         });
         setEditCopy(itemCopy);
      }
   }

   function cancelEdit() {
      setEditIndex(null);
      if (_.isFunction(props.setIsEditing)) {
         props.setIsEditing(false);
      }
      if (userCtx.openLineItem >= 0) {
         userCtx.setOpenLineItem(null);
      }
      setEditCopy(null);
   }

   function getSelectedSiteDescription(item) {
      const selectedSite = _.find(item.possibleOrderSites, (site) => site.siteId === item.orderSite);
      if (selectedSite) {
         return selectedSite.siteWithDescription;
      }
      return "";
   }

   function handleChange(field, value) {
      const copyUpdate = _.assign({}, editCopy);
      copyUpdate[field] = value;
      setEditCopy(copyUpdate);
   }

   function handleDelete(item) {
      if (allowInlineEditsDeletes) {
         props.deleteCallback(item);
         setConfirmDelete(false);
         setDeleteIndex(null);
      }
   }

   function handleEditClick(index) {
      if (allowInlineEditsDeletes) {
         if (editIndex === index) {
            updateCartItem();
         } else {
            allowEdit(index);
         }
      } else {
         userCtx.toggleCartOpen(true);
         userCtx.setOpenLineItem(index);
      }
   }

   function handleOptionEnabled(opt) {
      // Consigned customers can only order from their own site
      return (currentUser.isConsignedCustomerRole && opt !== currentUser.customerId);
   }

   function showConfirmModal(index) {
      setConfirmDelete(true);
      setDeleteIndex(index);
   }

   function toggleZeroPrice(index) {
      const itemsCopy = _.slice(cartItems);
      const editCopy = itemsCopy[index];
      editCopy.zeroPrice = !editCopy.zeroPrice;
      itemsCopy[index] = editCopy;
      setCartItems(itemsCopy);
      if (allowInlineEditsDeletes) {
         props.updateCallback(editCopy);
      }
   }

   function updateCartItem() {
      const itemsCopy = _.slice(cartItems);
      itemsCopy[editIndex] = editCopy;
      setCartItems(itemsCopy);
      if (currentUser
         && (currentUser.isAPUCS || currentUser.isSalesperson)
         && validateLineItemQty(editCopy.quantity, editCopy.realMinOrderQty, editCopy.baseUnit)
      ) {
         cancelEdit();
         if (allowInlineEditsDeletes) props.updateCallback(editCopy);
      } else {
         alertMinQty(editCopy.customerItemNumber, editCopy.realMinOrderQty, editCopy.baseUnit);
      }
   }

   return (
      <>
         <Row className="mt-3" style={{ position: "relative", width: "100%" }}>
            <Col xs={3}>
               <span className="h5 ms-3">Order Site: {props.siteName}</span>
            </Col>
            {showOverride ? (
               <Col xs={6}>
                  <Input
                     type="checkbox"
                     value={props.approvePriceOverride}
                     onChange={() => { props.priceOverrideCallback(props.siteName); }}
                  />
                  <Label className="ms-2">Override minimum price?</Label>
               </Col>
            ) : (
               <Col xs={6}>{""}</Col>
            )}
            <Col xs={3} className="cartMenuRightHeader">
               <span className="h5 float-end">{`Cart Subtotal: ${helpers.formatCurrency(cardSubtotal)}`}</span>
            </Col>
         </Row>
         <Row style={{ position: "relative", width: "100%" }}>
            <Col>
               <Table className="data-table" size="sm">
                  <thead className="table-header">
                     <tr>
                        <th width="7%">Customer PN</th>
                        <th width="7%">Customer Desc</th>
                        <th width="7%">ST Item</th>
                        <th width="4%">Price</th>
                        <th width="5%">Qty</th>
                        <th width="8%">Req. Ship Date</th>
                        <th width="10%">Order Site</th>
                        <th width="14%">Notes</th>
                        <th width="6%">Total</th>
                        <th width="6%">Site Amount</th>
                        {userCtx.currentUser &&
                           userCtx.currentUser.isAPUCS &&
                           props.siteName.endsWith("Sample") && (
                              <th width="5%">Zero Price</th>
                           )}
                        <th width="6%" />
                     </tr>
                  </thead>
                  <tbody>
                     {_.map(cartItems, (item, index) => {
                        const editingThisItem = (editIndex === index && allowInlineEditsDeletes);
                        return (
                           <tr
                              className={`cartItem tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                              key={index}
                           >
                              <td className="data-cell">{item.customerItemNumber}</td>
                              <td className="data-cell">{item.customerItemDescription}</td>
                              {/* <td className="data-cell">{item.itemNumber}</td> */}
                              {!isCustomer ? (
                                 <ItemDropdownMenu
                                    itemNumber={item.itemNumber}
                                    itemDescription={item.itemWithDesc}
                                    attachmentCount={item.itemAttachmentCount}
                                 />
                              ) : (
                                 <td className="data-cell">{item.customerItemDescription}</td>
                              )}
                              <td className="data-cell">{helpers.formatCurrency(item.itemPrice)}</td>
                              <td className="data-cell">
                                 {editingThisItem ? (
                                    <DebounceInput
                                       type="number"
                                       onChange={(e) => {
                                          setEditCopy({
                                             ...editCopy,
                                             quantity: e.target.value
                                                ? parseInt(e.target.value)
                                                : 0,
                                          });
                                       }}
                                       value={editCopy.quantity === "" ? 0 : _.trimStart(editCopy.quantity, "0")}
                                       debounceTimeout={300}
                                    />
                                 ) : (
                                    item.quantity
                                 )}
                              </td>
                              <td className="data-cell">
                                 {editingThisItem ? (
                                    <SingleDatePicker
                                       minDate={new Date().setDate(new Date().getDate() + 1)}
                                       selected={(editCopy && editCopy.requiredDate) || ""}
                                       onChangeCallback={(e) => handleChange("requiredDate", e)}
                                    />
                                 ) : (
                                    dateHelpers.toLongDateString(item.requiredDate)
                                 )}
                              </td>
                              <td className="data-cell">
                                 {editingThisItem ? (
                                    <Select
                                       classNamePrefix="serviceThreadSelect"
                                       options={_.filter(editCopy.siteDLL, (site) => site.value !== editCopy.orderSite)}
                                       value={_.find(editCopy.siteDLL, (site) => site.value === editCopy.orderSite)}
                                       setValue={() =>
                                          _.find(editCopy.siteDLL, (site) => {
                                             return site.value === editCopy.orderSite;
                                          })
                                       }
                                       onChange={(e) => handleChange("orderSite", e.value)}
                                       isOptionDisabled={(opt) => handleOptionEnabled(opt.value)}
                                    />
                                 ) : getSelectedSiteDescription(item)
                                 }
                              </td>
                              <td className="data-cell">
                                 {editingThisItem ? (
                                    <DebounceInput
                                       element="textarea"
                                       value={editCopy.notes}
                                       onChange={(e) => handleChange("notes", e.target.value)}
                                       debounceTimeout={300}
                                    />
                                 ) : (
                                    item.notes
                                 )}
                              </td>
                              <td className="data-cell">{helpers.formatCurrency(item.extendedPrice)}</td>
                              <td className="data-cell">
                                 {_.map(item.possibleOrderSites, (site) => {
                                    return (
                                       <span key={site.siteId}
                                          className="d-block"
                                       >{`${site.siteId} (${site.quantityAtSite})`}</span>
                                    );
                                 })}
                              </td>
                              {userCtx.currentUser &&
                                 userCtx.currentUser.isAPUCS &&
                                 props.siteName.endsWith("Sample") && (
                                    <td className="data-cell">
                                       <Input
                                          type="checkbox"
                                          checked={item.zeroPrice}
                                          onChange={() => toggleZeroPrice(index)}
                                       />
                                    </td>
                                 )}
                              <td className="data-cell">
                                 {allowInlineEditsDeletes ? (
                                    <ButtonGroup>
                                       <Button
                                          className="icon-button me-2 float-end"
                                          title={editingThisItem ? "Save changes" : "Edit line"}
                                          color={editingThisItem ? "success" : "info"}
                                          onClick={() => handleEditClick(index)}
                                          disabled={
                                             (editIndex !== null || props.isEditing) &&
                                             editIndex !== index
                                          }
                                       >
                                          <FontAwesomeIcon
                                             className="whiteIcon"
                                             icon={editingThisItem ? "check" : "edit"}
                                          />
                                       </Button>
                                       <Button
                                          className="icon-button float-end me-1"
                                          color={editingThisItem ? "secondary" : "danger"}
                                          title={editingThisItem ? "Discard Changes" : "Remove From Cart"}
                                          onClick={
                                             editingThisItem
                                                ? () => cancelEdit()
                                                : () => showConfirmModal(index)
                                          }
                                          disabled={
                                             (editIndex !== null || props.isEditing) &&
                                             editIndex !== index
                                          }
                                       >
                                          <FontAwesomeIcon
                                             className="whiteIcon"
                                             icon={editingThisItem ? "times" : "trash"}
                                          />
                                       </Button>
                                    </ButtonGroup>
                                 ) : null}
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
                  <tfoot className="table-footer">
                     <tr>
                        <td colSpan="8" />
                        <td className="fw-bold" children={helpers.formatCurrency(cardSubtotal)} />
                        <td colSpan="3" />
                     </tr>
                  </tfoot>
               </Table>
            </Col>
         </Row>

         <div style={{ zIndex: 100000 }}>
            <Modal id="confirm-delete" isOpen={confirmDelete} contentClassName={modalClassName} >
               <ModalHeader children="Confirm Delete" />
               <ModalBody>
                  Are you sure you want to remove the item
                  <br />
                  <strong>
                     {deleteIndex !== null && cartItems[deleteIndex].customerItemDescription}
                  </strong>
                  <br /> from the cart?
               </ModalBody>
               <ModalFooter>
                  <ButtonGroup>
                     <Button
                        children="Yes"
                        className="button-success"
                        onClick={() => { handleDelete(cartItems[deleteIndex]); }}
                     />
                     <Button
                        children="No"
                        className="button-danger"
                        onClick={() => {
                           setConfirmDelete(false);
                           setDeleteIndex(null);
                        }}
                     />
                  </ButtonGroup>
               </ModalFooter>
            </Modal>
         </div>
      </>
   );
}
